import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  InputGroup,
  Alert,
  ListGroup,
  ListGroupItem,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faLink } from "@fortawesome/free-solid-svg-icons";
import { Toast } from "./../UI/toast/Toast";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import {
  findExistingOwner,
  linkPropertyToOwner,
} from "../../helpers/api/condominium";

/**
 * HO-HU-37: vincula una property a una cuenta de propietario que YA
 * EXISTE (misma persona con otra unidad, en este condominio o en otro), en
 * vez de crearle una cuenta nueva.
 *
 * Búsqueda por usuario o cédula EXACTOS a propósito -- no es un buscador
 * general de propietarios, el manager tiene que saber el dato exacto de la
 * persona que busca (ver PropertyController::findOwner en el backend).
 */
const LinkOwnerModal = ({ isOpen, toggle, property, onLinked }) => {
  const [identifier, setIdentifier] = useState("");
  const [searching, setSearching] = useState(false);
  const [found, setFound] = useState(null);
  const [linking, setLinking] = useState(false);

  const reset = () => {
    setIdentifier("");
    setFound(null);
  };

  const handleClose = () => {
    reset();
    toggle();
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) return;

    setSearching(true);
    setFound(null);
    try {
      const { data } = await findExistingOwner(identifier.trim(), property?.id);
      setFound(data);
    } catch (error) {
      handleErrorMessage(error);
    } finally {
      setSearching(false);
    }
  };

  const handleLink = async () => {
    setLinking(true);
    try {
      await linkPropertyToOwner(property.id, found.authCredential.id);
      Toast.fire({
        type: "success",
        title: "Propiedad vinculada exitosamente.",
      });
      onLinked && onLinked();
      handleClose();
    } catch (error) {
      handleErrorMessage(error);
    } finally {
      setLinking(false);
    }
  };

  const alreadyOwner = found?.authCredential?.id === property?.user?.id;
  const currentDni = property?.user?.data?.dni || null;
  const foundDni = found?.authCredential?.data?.dni || null;
  // HO-HU-37: el backend (linkOwner) exige que ambas cedulas coincidan --
  // se muestra aca ANTES de intentar vincular para no hacerle mandar el
  // request al manager si igual va a rechazarlo el servidor.
  const dniMismatch =
    !!found && !alreadyOwner && (!currentDni || !foundDni || currentDni !== foundDni);
  const blockedReason = !alreadyOwner && !dniMismatch ? found?.linkBlockedReason || null : null;

  return (
    <Modal isOpen={isOpen} toggle={handleClose}>
      <ModalHeader toggle={handleClose}>
        Vincular a un propietario existente
      </ModalHeader>
      <ModalBody>
        <p className="fs--1 text-muted mb-1">
          Si esta persona ya tiene una cuenta (porque tiene otra unidad en
          este condominio o en otro), búscala por su usuario o su cédula
          exactos para vincularle{" "}
          <strong>{property?.property || "esta propiedad"}</strong>
        </p>
        <p className="fs--1 text-muted mb-3">
          Cédula actual de esta propiedad:{" "}
          <strong>{currentDni || "no registrada"}</strong>
        </p>
        <form onSubmit={handleSearch}>
          <InputGroup>
            <Input
              placeholder="Usuario o cédula exactos"
              value={identifier}
              onChange={(e) => {
                const value = e.target.value;
                setIdentifier(value);
                if (value.trim() === "") setFound(null);
              }}
              disabled={searching}
            />
            <Button
              color="secondary"
              type="submit"
              disabled={searching || !identifier.trim()}
            >
              <FontAwesomeIcon icon={faSearch} className="mr-1" />
              {searching ? "Buscando..." : "Buscar"}
            </Button>
          </InputGroup>
        </form>

        {found && (
          <Alert
            color={alreadyOwner ? "warning" : dniMismatch || blockedReason ? "danger" : "info"}
            className="mt-3 mb-0"
          >
            <p className="mb-1">
              <strong>
                {found.authCredential.data.firstname}{" "}
                {found.authCredential.data.lastname}
              </strong>{" "}
              ({found.authCredential.username}) — Cédula:{" "}
              <strong>{foundDni || "no registrada"}</strong>
            </p>
            {alreadyOwner ? (
              <p className="fs--1 mb-0">
                Esta propiedad ya pertenece a esta cuenta.
              </p>
            ) : dniMismatch ? (
              <p className="fs--1 mb-0">
                La cédula de esta propiedad ({currentDni || "no registrada"})
                no coincide con la de esta cuenta. Para evitar vincular a la
                persona equivocada, primero edita la propiedad y corrige la
                cédula para que coincidan, y vuelve a intentar.
              </p>
            ) : blockedReason ? (
              <p className="fs--1 mb-0">{blockedReason}</p>
            ) : found.properties.length > 0 ? (
              <>
                <p className="fs--1 mb-1">Ya tiene vinculadas:</p>
                <ListGroup className="fs--1">
                  {found.properties.map((p) => (
                    <ListGroupItem key={p.id} className="py-1 px-2">
                      {p.property || "(sin identificar)"} —{" "}
                      {p.condominiumName || "Condominio"}
                    </ListGroupItem>
                  ))}
                </ListGroup>
              </>
            ) : (
              <p className="fs--1 mb-0">
                Todavía no tiene ninguna propiedad vinculada.
              </p>
            )}
          </Alert>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" size="sm" onClick={handleClose}>
          Cancelar
        </Button>
        <Button
          color="primary"
          size="sm"
          onClick={handleLink}
          disabled={!found || alreadyOwner || dniMismatch || !!blockedReason || linking}
        >
          <FontAwesomeIcon icon={faLink} className="mr-1" />
          {linking ? "Vinculando..." : "Vincular"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

LinkOwnerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  property: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    property: PropTypes.string,
    user: PropTypes.shape({ id: PropTypes.number }),
  }),
  onLinked: PropTypes.func,
};

export default LinkOwnerModal;
